import { useEffect, useState, type ReactElement } from 'react';
import { Alert, Box, Group, Loader, Stack } from '@mantine/core';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { UsageCategoriesViewModel } from './UsageCategoriesViewModel';
import TaxonomyToolbar from './TaxonomyToolbar';
import TaxonomyTreeTable from './TaxonomyTreeTable';
import TaxonomyChart from './TaxonomyChart';
import UsageCategoryAssistModal from './UsageCategoryAssistModal';
import type { CategoriesView, TaxonomyRowView } from './UsageCategoryTaxonomyTypes';

export default function UsageCategoriesPage(): ReactElement {
    const vm = useViewModel(UsageCategoriesViewModel);
    const [view, setView] = useState<CategoriesView>('list');
    const [focusedDraftId, setFocusedDraftId] = useState<string | null>(null);
    const [editingDraftId, setEditingDraftId] = useState<string | null>(null);

    useEffect(() => {
        void vm.initialize();
    }, [vm]);

    const rows = vm.visibleRows;

    useEffect(() => {
        if (rows.length === 0) return;
        if (focusedDraftId === null || !rows.some((r) => r.node.draftId === focusedDraftId)) {
            setFocusedDraftId(rows[0]?.node.draftId ?? null);
        }
    }, [rows, focusedDraftId]);

    useEffect(() => {
        if (editingDraftId !== null && !rows.some((r) => r.node.draftId === editingDraftId)) {
            setEditingDraftId(null);
        }
    }, [rows, editingDraftId]);

    const addCategory = (): void => {
        const id = vm.addCategory('New category');
        setFocusedDraftId(id);
        setEditingDraftId(id);
    };

    const addChild = (parentDraftId: string): void => {
        const id = vm.addSubcategory(parentDraftId, 'New subcategory');
        if (id !== null) {
            setFocusedDraftId(id);
            setEditingDraftId(id);
        }
    };

    const removeNode = (draftId: string): void => {
        const index = rows.findIndex((r) => r.node.draftId === draftId);
        const neighbor = nextFocusAfterRemoval(rows, index);
        setEditingDraftId(null);
        vm.removeNode(draftId);
        setFocusedDraftId(neighbor);
    };

    const revert = (): void => {
        setEditingDraftId(null);
        vm.revert();
    };

    const applyAssist = (): void => {
        const firstAddedId = vm.applySelectedSuggestions();
        if (firstAddedId !== null) {
            setEditingDraftId(null);
            setFocusedDraftId(firstAddedId);
        }
    };

    return (
        <Box pb="xl">
            <PageHeaderComponent
                title="Usage Categories"
                subtitle="The category taxonomy the classifier assigns to each AI session. Edit as a list, or switch to the chart and drag nodes to restructure it — either way it saves as the classification taxonomy."
            />

            {vm.loadError && (
                <Alert color="red" title="Failed to load" mb="md">
                    {vm.loadError}
                </Alert>
            )}

            {vm.notSeeded && (
                <Alert color="blue" title="No usage categories yet" mb="md">
                    The usage-category taxonomy has not been seeded for this tenant yet. Add a category to get started, or run the context seed to populate the defaults.
                </Alert>
            )}

            {vm.saveError && (
                <Alert color="red" title="Save failed" mb="md">
                    {vm.saveError}
                </Alert>
            )}

            {vm.loading ? (
                <Group justify="center" py="xl">
                    <Loader size="sm" />
                </Group>
            ) : (
                !vm.loadError &&
                !vm.notSeeded && (
                    <Stack gap="md">
                        <TaxonomyToolbar
                            stats={vm.stats}
                            isDirty={vm.isDirty}
                            saving={vm.saving}
                            pendingChangeCount={vm.pendingChangeCount}
                            filterText={vm.filterText}
                            isFiltering={vm.isFiltering}
                            matchCount={vm.matchCount}
                            view={view}
                            includeGeneral={vm.includeGeneral}
                            canGenerate={vm.canGenerateFromDirectory}
                            isGenerating={vm.isGenerating}
                            generationPhase={vm.generationPhase}
                            generationProgress={vm.generationProgress}
                            onFilterChange={(value) => vm.setFilter(value)}
                            onViewChange={setView}
                            onToggleGeneral={(value) => vm.setIncludeGeneral(value)}
                            onGenerateFromDirectory={() => void vm.generateFromDirectory()}
                            onUseDefault={() => vm.loadDefaultSeed()}
                            onAddCategory={addCategory}
                            onExpandAll={() => vm.expandAll()}
                            onCollapseAll={() => vm.collapseAll()}
                            onRevert={revert}
                            onSave={() => void vm.save()}
                        />

                        {vm.generationError && (
                            <Alert color="red" title="Couldn't generate categories">
                                {vm.generationError}
                            </Alert>
                        )}
                        {vm.generationResult && (
                            <Alert color="teal" title="Categories generated">
                                {vm.generationResult} Review the draft, then Save.
                            </Alert>
                        )}
                        {vm.categories.length === 0 && !vm.isGenerating ? (
                            <Alert color="blue" title="No usage categories yet">
                                Hit Generate from directory to build categories from your org and fill each with AI-generated usage tasks, Use
                                default categories for a general-purpose starter set, or add one manually with New category.
                            </Alert>
                        ) : view === 'chart' ? (
                            <TaxonomyChart vm={vm} onAssist={(id) => vm.openAssist(id)} />
                        ) : (
                            <TaxonomyTreeTable
                                rows={rows}
                                focusedDraftId={focusedDraftId}
                                editingDraftId={editingDraftId}
                                onFocusRow={(id) => setFocusedDraftId(id)}
                                onStartEdit={(id) => {
                                    setFocusedDraftId(id);
                                    setEditingDraftId(id);
                                }}
                                onStopEdit={() => setEditingDraftId(null)}
                                onSetExpanded={(id, value) => vm.setExpanded(id, value)}
                                onRename={(id, label) => vm.renameNode(id, label)}
                                onToggleEnabled={(id) => vm.toggleEnabled(id)}
                                onRemove={removeNode}
                                onAddChild={addChild}
                                onAssist={(id) => vm.openAssist(id)}
                            />
                        )}
                    </Stack>
                )
            )}

            <UsageCategoryAssistModal vm={vm} onApply={applyAssist} />
        </Box>
    );
}

function nextFocusAfterRemoval(rows: TaxonomyRowView[], index: number): string | null {
    if (index < 0) return null;
    const removed = rows[index];
    if (!removed) return null;
    let after = index + 1;
    while (after < rows.length && (rows[after]?.depth ?? 0) > removed.depth) after += 1;
    const successor = rows[after];
    if (successor) return successor.node.draftId;
    const predecessor = rows[index - 1];
    return predecessor ? predecessor.node.draftId : null;
}
